// bm-design-system: switch primitive
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const switchTrack = cva(
  [
    "relative inline-flex shrink-0 cursor-pointer items-center rounded-full border transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-page",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-5 w-9",
        md: "h-6 w-11",
      },
      checked: {
        true: "border-success/40 bg-success-faded",
        false: "border-hairline bg-surface",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const switchThumb = cva(
  "pointer-events-none rounded-full shadow-sm transition-transform",
  {
    variants: {
      size: {
        sm: "h-3.5 w-3.5",
        md: "h-4 w-4",
      },
      checked: {
        true: "bg-success",
        false: "bg-danger",
      },
    },
    compoundVariants: [
      { size: "sm", checked: false, className: "translate-x-[3px]" },
      { size: "sm", checked: true, className: "translate-x-[17px]" },
      { size: "md", checked: false, className: "translate-x-1" },
      { size: "md", checked: true, className: "translate-x-[22px]" },
    ],
    defaultVariants: {
      size: "md",
    },
  },
);

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange">,
    Omit<VariantProps<typeof switchTrack>, "checked"> {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, label, size, className, disabled, ...props }, ref) => {
    const control = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={cn(switchTrack({ size, checked }), className)}
        {...props}
      >
        <span aria-hidden className={switchThumb({ size, checked })} />
      </button>
    );

    if (!label) return control;

    return (
      <label className="inline-flex cursor-pointer items-center gap-2 font-normal text-ink-body">
        {control}
        {label}
      </label>
    );
  },
);
Switch.displayName = "Switch";

export { Switch };
